/**
 * 匿名 ID 管理模块
 *
 * 提供匿名用户 ID 的获取、确保和清除功能
 */
import { deleteCookie, getCookie, setCookie } from './cookie';
import { resolveApiUrl } from '../services/runtimeApiConfig';

const ANONYMOUS_ID_COOKIE = 'anonymous_id';
const ANONYMOUS_ID_PATTERN = /^[a-f0-9]{32}$/;

/**
 * 获取匿名 ID
 *
 * 从 Cookie 中读取匿名 ID，如果不存在或格式无效返回 null
 *
 * @returns 匿名 ID 或 null
 */
export function getAnonymousId(): string | null {
  const existingId = getCookie(ANONYMOUS_ID_COOKIE);
  if (existingId && ANONYMOUS_ID_PATTERN.test(existingId)) {
    return existingId;
  }
  return null;
}

/**
 * 确保匿名 ID 存在
 *
 * 如果 Cookie 中已有有效的匿名 ID 则直接返回，
 * 否则调用后端 API 生成新的匿名 ID 并保存到 Cookie
 *
 * @returns 匿名 ID
 */
export async function ensureAnonymousId(): Promise<string> {
  const existingId = getAnonymousId();
  if (existingId) {
    return existingId;
  }

  // 调用后端 API 生成匿名 ID
  const response = await fetch(resolveApiUrl('/api/v1/chat/anonymous-id'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to generate anonymous ID');
  }

  const data = await response.json();
  const newId = data?.data?.anonymous_id;
  if (typeof newId !== 'string' || !ANONYMOUS_ID_PATTERN.test(newId)) {
    throw new Error('Invalid anonymous ID returned from server');
  }

  // 保存到 Cookie (1年有效期)
  setCookie(ANONYMOUS_ID_COOKIE, newId, 365);
  return newId;
}

/**
 * 清除匿名 ID
 *
 * 登录后调用，清除 Cookie 中的匿名 ID
 */
export function clearAnonymousId(): void {
  deleteCookie(ANONYMOUS_ID_COOKIE);
}
